/**
 * One photograph, large, with everything recorded about it.
 *
 * The controls here are the client's main test of the capability list: a
 * contributor gets the same photograph and the same metadata, and simply has no
 * hide control on the page. Nothing is rendered disabled — a disabled button is
 * still a promise that the reader could have that authority, and they cannot.
 */

import { h, replace, clear } from "./dom";
import { icon } from "./icons";
import { api, blobUrl, cropUrl } from "./api";
import { store } from "./state";
import { toast } from "./ui";
import { ask, pickPeople } from "./modal";
import { darkroom } from "./editpanel";
import { pageSplit } from "./pagesplit";
import { videoPlayer } from "./videoplayer";
import { count, duration, formatDate, personName, precisionLabel, isFuzzy } from "./format";
import type { AssetCard, AssetFace, PersonSummary, Precision } from "./types";

let host: HTMLElement | null = null;
let onKey: ((e: KeyboardEvent) => void) | null = null;
let activeVideo: HTMLVideoElement | null = null;

export function closeAsset(): void {
  activeVideo?.pause();
  activeVideo = null;
  host?.remove();
  host = null;
  if (onKey) document.removeEventListener("keydown", onKey);
  onKey = null;
}

export function openAsset(a: AssetCard): void {
  closeAsset();
  const src = blobUrl(a.contentHash);
  /**
   * The 1280px JPEG made at upload, where there is one.
   *
   * Every other screen in this client draws from `proxyKey ?? contentHash`;
   * this one asked for the original alone, which is right for a detail view
   * and wrong for HEIC. `createImageBitmap` and `<img>` both refuse HEIC
   * outside Safari — that is why the proxy exists at all — so on the archive
   * this was built for, opening a photograph showed a broken-image icon while
   * its own thumbnail sat correctly in the grid behind it.
   *
   * The original is still what loads first, because the whole point of opening
   * a photograph is to see it properly. The proxy is what it falls back to.
   */
  const proxy = blobUrl(a.proxyKey ?? null);

  const playback = a.kind === "video"
    ? videoPlayer({
        src,
        // The poster is the cover frame where the upload made one; a video's
        // own first frame is often black.
        poster: proxy ?? src,
        className: "player",
      })
    : null;
  activeVideo = playback?.video ?? null;
  const media = playback?.el ?? h("img", { class: "big", src: src ?? "", alt: a.caption ?? a.fileName });

  // One shot, and only for the original. The darkroom swaps this same element's
  // `src` to a rendition as the sliders move, and a fallback still armed then
  // would answer a failed render by silently showing the unedited photograph —
  // which looks like the edit did nothing rather than like it failed.
  let disarmFallback: (() => void) | null = null;
  if (a.kind !== "video" && proxy && proxy !== src) {
    const img = media as HTMLImageElement;
    const fallback = (): void => {
      disarmFallback?.();
      img.src = proxy;
    };
    disarmFallback = () => {
      img.removeEventListener("error", fallback);
      disarmFallback = null;
    };
    img.addEventListener("error", fallback);
    // Disarmed as soon as the original decodes, so it cannot fire later.
    img.addEventListener("load", () => disarmFallback?.(), { once: true });
  }

  const meta = h("dl", { class: "kv" });
  const add = (k: string, v: Node | string): void => {
    meta.appendChild(h("dt", { text: k }));
    meta.appendChild(h("dd", null, v));
  };
  const drawMeta = (): void => {
    clear(meta);
    add("Taken", formatDate(a.capturedAt, a.capturedPrecision));
    add(
      "Precision",
      h(
        "span",
        { class: isFuzzy(a.capturedPrecision) ? "prec fuzzy" : "prec" },
        precisionLabel(a.capturedPrecision),
      ),
    );
    if (a.dateSource) add("Dated by", a.dateSource.replace(/-/g, " "));
    add("File", a.fileName);
    if (a.width && a.height) add("Size", `${a.width} × ${a.height}`);
    if (a.kind === "video" && a.durationMs) add("Runs", duration(a.durationMs));
    if (a.storyCount) add("Stories", String(a.storyCount));
  };
  drawMeta();

  const canEditMetadata = store.can("asset.metadata.edit");
  const title = h("h3", {
    text: a.caption ?? a.fileName,
    title: canEditMetadata ? "Double-click to change the title" : undefined,
    class: canEditMetadata ? "lbtitle editable" : "lbtitle",
  });
  const description = h("button", {
    class: "lbdescription",
    type: "button",
    text: a.description || "Add a description",
    hidden: !canEditMetadata && !a.description,
  });

  const editText = async (field: "caption" | "description"): Promise<void> => {
    const previous = a[field];
    const answer = await ask({
      title: field === "caption" ? "Photograph title" : "Description",
      fields: [{
        name: "value",
        label: field === "caption" ? "Title" : "Description",
        value: previous ?? "",
        multiline: field === "description",
        maxLength: field === "caption" ? 160 : 1200,
      }],
      confirm: "Save",
    });
    if (!answer) return;
    const value = answer.value?.trim() || null;
    a[field] = value;
    title.textContent = a.caption ?? a.fileName;
    description.textContent = a.description || "Add a description";
    try {
      await api.patchMetadata(store.libraryId, a.id, { [field]: value });
    } catch (error) {
      a[field] = previous;
      title.textContent = a.caption ?? a.fileName;
      description.textContent = a.description || "Add a description";
      toast(error instanceof Error ? error.message : "could not save that", true);
    }
  };
  if (canEditMetadata) {
    title.addEventListener("dblclick", () => void editText("caption"));
    description.addEventListener("click", () => void editText("description"));
  }

  const editDate = async (): Promise<void> => {
    const answer = await ask({
      title: "When was this taken?",
      fields: [
        {
          name: "year",
          label: "Year",
          value: a.capturedAt?.slice(0, 4) ?? "",
          required: true,
        },
        {
          name: "precision",
          label: "How exact is it?",
          value: a.capturedPrecision ?? "year",
          options: [
            ["year", "Exact year"],
            ["circa", "Around this year"],
            ["decade", "This decade"],
          ],
        },
      ],
      confirm: "Save date",
    });
    const year = Number(answer?.year);
    if (!answer || !Number.isInteger(year) || year < 1800 || year > new Date().getFullYear()) {
      if (answer) toast("Enter a valid year.", true);
      return;
    }
    const previous = {
      capturedAt: a.capturedAt,
      capturedPrecision: a.capturedPrecision,
      dateSource: a.dateSource,
    };
    const precision = (answer.precision || "year") as Precision;
    a.capturedAt = `${precision === "decade" ? Math.floor(year / 10) * 10 : year}-01-01`;
    a.capturedPrecision = precision;
    a.dateSource = "user";
    drawMeta();
    try {
      await api.setDate(store.libraryId, a.id, a.capturedAt, precision);
    } catch (error) {
      Object.assign(a, previous);
      drawMeta();
      toast(error instanceof Error ? error.message : "could not save that", true);
    }
  };

  /**
   * Naming somebody, from the photograph you are looking at.
   *
   * This is where names actually get typed. The People screen is a wall of
   * faces out of context and the answer to "who is that" usually comes from
   * the photograph — the room, the others in it, the year — which is exactly
   * what you have in front of you here and nowhere else.
   */
  const nameHere = (identityId: string): void => {
    void (async () => {
      const answer = await ask({
        title: "Who is this?",
        body:
          "The name is yours — another member of the family can call the same " +
          "person something else. Naming them here names them in every " +
          "photograph they appear in.",
        fields: [
          { name: "name", label: "Name", placeholder: "Mary", required: true, maxLength: 80 },
        ],
        confirm: "Name them",
      });
      const name = answer?.name?.trim();
      if (!name) return;
      try {
        await api.nameIdentity(store.libraryId, identityId, name);
        toast(`Named ${name}.`);
        // Redrawn rather than closed. A photograph of four people needs four
        // names, and closing after each one means finding it again three
        // times.
        for (const p of a.people) if (p.identityId === identityId) p.name = name;
        void drawPeople();
        store.named();
      } catch (e) {
        toast(e instanceof Error ? e.message : "could not name them", true);
      }
    })();
  };

  const canName = store.can("identity.name");

  const people = h("div", { class: "lbpeople" });

  /**
   * Who is in this photograph, with their faces.
   *
   * The face and not only the name, because "who is that" is a question you
   * answer by looking. A column of text chips reading "Unnamed, Unnamed,
   * Unnamed" tells somebody there are three people and nothing else — and this
   * is the screen where names actually get typed, so it is the screen that
   * most needs to show what is being named.
   *
   * Drawn from `/people` rather than from the card, and re-drawn whenever an
   * identity changes, so naming somebody here updates the chip in place
   * instead of requiring the photograph to be closed and opened again.
   */
  const drawPeople = async (): Promise<void> => {
    if (!a.people.length) {
      replace(people, h("span", { class: "muted", text: "Nobody named in this one yet." }));
      return;
    }

    // Faces come from the people index, which carries a crop per person; the
    // card carries only names. One request, cached by the browser, rather than
    // one per face.
    let faces = new Map<string, string | null>();
    let names = new Map<string, string | null>();
    try {
      for (const p of await api.people(store.libraryId)) {
        faces.set(p.identityId, p.coverCrop);
        names.set(p.identityId, p.name);
      }
    } catch {
      // No faces is a worse screen, not a broken one.
    }

    replace(
      people,
      ...a.people.map((p) => {
        // The freshest name wins: the card was fetched when the grid last
        // loaded and somebody may have named this person since.
        const name = names.has(p.identityId) ? (names.get(p.identityId) ?? null) : p.name;
        const named = !!name;
        const crop = cropUrl(faces.get(p.identityId) ?? null);

        const displayName = personName(name);
        return h(
          "button",
          {
            class: named ? "lbperson" : "lbperson unnamed",
            title: named
              ? `${displayName} — narrow the archive to this person`
              : canName
                ? `${displayName} — click to name them`
                : displayName,
            "aria-label":
              named || !canName
                ? displayName
                : `${displayName}, click to name them`,
            onclick: () => {
              if (!named && canName) {
                nameHere(p.identityId);
                return;
              }
              store.togglePerson(p.identityId);
              closeAsset();
            },
          },
          crop
            ? h("img", { src: crop, alt: "", loading: "lazy" })
            : h("span", { class: "lbface-none", text: "?" }),
        );
      }),
    );
  };
  void drawPeople();

  // --- the faces on the photograph ----------------------------------------

  /**
   * The detector's rectangles, over the picture.
   *
   * The chips below say *who is in* this photograph. They cannot say which
   * face is whose, and they say nothing at all about a face the detector found
   * and nobody has placed — so the one question you actually have in front of
   * a family photograph, "who is the woman on the left", had no control
   * anywhere in this client. The desktop drew the boxes; this did not.
   *
   * Positioned in percentages because `bbox` is already fractions of the
   * image — `bigEnough` multiplies by width and height to get pixels — so the
   * boxes track the photograph through every reflow and every viewport without
   * measuring anything.
   */
  const facesLayer = h("div", { class: "lbfaces", hidden: true });
  let facesOn = true;
  try {
    facesOn = localStorage.getItem("heirloom.faces") !== "off";
  } catch {
    /* the default is fine */
  }

  /**
   * Showing the boxes is a preference, because they sit on top of the
   * photograph — which is the thing somebody opened this to look at. Remembered,
   * so it is answered once rather than on every photograph.
   */
  const facesToggle = h("button", {
    class: "ib",
    hidden: true,
    "aria-label": "Show the faces found on this photograph",
    onclick: () => {
      facesOn = !facesOn;
      try {
        localStorage.setItem("heirloom.faces", facesOn ? "on" : "off");
      } catch {
        /* it just will not be remembered */
      }
      void drawFaces();
    },
  });
  facesToggle.appendChild(icon("people"));

  /**
   * "Who is this?", asked of one rectangle.
   *
   * One dialog, and the same one for a face that is already placed: correcting
   * a wrong name is the same question as answering an unasked one, and a
   * separate "change" path would be a second control doing the first one's job.
   */
  const whoIsThis = async (f: AssetFace): Promise<void> => {
    if (!store.can("identity.face.confirm")) {
      toast("You can look at this archive but not name people in it.", true);
      return;
    }
    let others: PersonSummary[] = [];
    try {
      others = await api.people(store.libraryId);
    } catch {
      /* a list of nobody still lets you make somebody new */
    }
    const NEW = "__new__";
    const NOBODY = "__none__";
    const picked = await pickPeople({
      title: f.name ? `Not ${personName(f.name)}?` : "Who is this?",
      choices: [
        { value: NEW, label: "Somebody new", detail: "Create and name a person" },
        ...others.map((person) => ({
          value: person.identityId,
          label: personName(person.name),
          detail: count(person.count, "photograph"),
          image: cropUrl(person.coverCrop),
        })),
        { value: NOBODY, label: "Leave unnamed", detail: "Keep this face unassigned" },
      ],
      selected: f.identityId ? [f.identityId] : [],
    });
    const who = picked?.[0];
    if (!who) return;
    let typed = "";
    if (who === NEW) {
      const named = await ask({
        title: "Name this person",
        fields: [{ name: "name", label: "Their name", placeholder: "Mary", required: true }],
        confirm: "Name them",
      });
      typed = named?.name?.trim() ?? "";
      if (!typed) return;
    }

    try {
      if (who === NOBODY) {
        await api.assignFace(store.libraryId, a.id, f.id, null);
        toast("Left unnamed.");
      } else if (who === NEW) {
        if (!typed) {
          toast("Somebody new needs a name.", true);
          return;
        }
        const existing = others.find(
          (person) => person.name?.trim().localeCompare(typed, undefined, { sensitivity: "accent" }) === 0,
        );
        if (existing) {
          const answer = await ask({
            title: `Another ${typed} already exists`,
            body: "Choose the existing person from the face list when they are the same person.",
            fields: [{
              name: "choice",
              label: "Create as",
              value: "separate",
              options: [["separate", "A different person with the same name"], ["existing", "Use the existing person"]],
            }],
            confirm: "Continue",
          });
          if (!answer) return;
          if (answer.choice === "existing") {
            await api.assignFace(store.libraryId, a.id, f.id, existing.identityId);
            toast(`That face is ${personName(existing.name)}.`);
            return;
          }
        }
        {
        // The identity exists before a face points at it, and is named after
        // the face is attached — so a failure part way through leaves a person
        // with a face rather than a name with nobody in it.
          const identityId = crypto.randomUUID();
          await api.createIdentity(store.libraryId, identityId);
          await api.assignFace(store.libraryId, a.id, f.id, identityId);
          await api.nameIdentity(store.libraryId, identityId, typed);
          toast(`Named ${typed}.`);
        }
      } else {
        await api.assignFace(store.libraryId, a.id, f.id, who);
        const to = others.find((o) => o.identityId === who);
        toast(`That face is ${personName(to?.name ?? null)}.`);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not save that", true);
      return;
    }

    // The card's own list of who is in this photograph is now behind, and the
    // chips below are drawn from it.
    try {
      const fresh = await api.assetDetail(store.libraryId, a.id);
      a.people = [
        ...new Map(
          fresh.faces
            .filter((x) => x.identityId)
            .map((x) => [x.identityId!, { identityId: x.identityId!, name: x.name }]),
        ).values(),
      ];
    } catch {
      /* the redraw below is still worth doing */
    }
    await drawFaces();
    void drawPeople();
    store.named();
  };

  async function drawFaces(): Promise<void> {
    // Film is excluded: a video's faces carry a timestamp, and every
    // appearance of everybody would stack onto one still.
    if (a.kind === "video") return;
    let faces: AssetFace[] = [];
    try {
      faces = (await api.assetDetail(store.libraryId, a.id)).faces;
    } catch {
      // A photograph that will not say what is on it is not a broken screen.
      return;
    }

    clear(facesLayer);
    facesLayer.hidden = !facesOn || !faces.length;
    facesToggle.hidden = !faces.length;
    facesToggle.classList.toggle("on", facesOn && faces.length > 0);
    const unplaced = faces.filter((f) => !f.name).length;
    facesToggle.title = !faces.length
      ? "No faces found on this photograph"
      : `${count(faces.length, "face")} found` +
        (unplaced ? `, ${unplaced} not placed` : "") +
        ` — ${facesOn ? "hide" : "show"} them`;

    for (const f of faces) {
      const named = !!f.name;
      const box = h("button", {
        class: named ? "lbface named" : "lbface unknown",
        style:
          `left:${f.bbox_x * 100}%;top:${f.bbox_y * 100}%;` +
          `width:${f.bbox_w * 100}%;height:${f.bbox_h * 100}%`,
        title: named
          ? `${personName(f.name)} — click to correct`
          : "Nobody has placed this face — click to say who it is",
        onclick: (e: Event) => {
          e.stopPropagation();
          void whoIsThis(f);
        },
      });
      box.appendChild(
        h("span", { class: "lbfacetag", text: named ? personName(f.name) : "?" }),
      );
      facesLayer.appendChild(box);
    }
  }

  void drawFaces();

  /**
   * Hide and close, as icons.
   *
   * They were "Hide from the library" and "Close" spelled out, which is
   * eighteen characters of button in a panel that also has to hold a
   * darkroom — at a narrow width they were pushed clean off it. The words move
   * to the tooltip, where they are still there for anybody who wants them and
   * take no room from anybody who does not.
   */
  const acts = h("div", { class: "lbacts" });
  acts.appendChild(facesToggle);
  if (src) {
    acts.appendChild(
      h("a", {
        class: "ib",
        href: src,
        download: a.fileName,
        title: "Download original",
        "aria-label": "Download original",
      }, icon("download")),
    );
  }
  if (store.can("asset.hide")) {
    acts.appendChild(
      h(
        "button",
        {
          class: a.hidden ? "ib on" : "ib",
          title: a.hidden ? "Show in the library again" : "Hide from the library",
          "aria-label": a.hidden ? "Unhide" : "Hide from the library",
          onclick: () => {
            void api
              .setHidden(store.libraryId, a.id, !a.hidden)
              .then(() => {
                a.hidden = !a.hidden;
                toast(a.hidden ? "Hidden." : "Visible again.");
                closeAsset();
              })
              .catch((e: unknown) =>
                toast(e instanceof Error ? e.message : "could not change that", true),
              );
          },
        },
        icon("eye-off"),
      ),
    );
  }
  acts.appendChild(
    h(
      "button",
      { class: "ib", title: "Close", "aria-label": "Close", onclick: () => closeAsset() },
      icon("x"),
    ),
  );

  // The darkroom, for anyone allowed to change what a photograph looks like.
  // Film is excluded: `edits::apply` renders a still, and offering a
  // brightness slider that silently does nothing to a video is worse than
  // offering nothing.
  const editing =
    a.kind !== "video" && store.can("asset.edit") ? darkroom(a) : null;
  if (editing) {
    editing.onPreview((url) => {
      // A rendition is about to replace the original, so the HEIC fallback has
      // nothing left to be right about — and leaving it armed would make a
      // failed render look like an edit that did nothing.
      disarmFallback?.();
      const img = media as HTMLImageElement;
      img.style.filter = "";
      img.style.transform = "";
      img.src = url;
    });
    editing.onTonePreview((edits) => {
      const img = media as HTMLImageElement;
      img.style.filter = [
        `brightness(${1 + (edits.brightness ?? 0)})`,
        `contrast(${1 + (edits.contrast ?? 0)})`,
        `saturate(${1 + (edits.saturation ?? 0)})`,
      ].join(" ");
    });
    editing.onRotatePreview((quarters) => {
      const img = media as HTMLImageElement;
      if (!quarters) {
        img.style.transform = "";
        return;
      }
      const frame = img.closest<HTMLElement>(".psframe");
      const width = img.offsetWidth;
      const height = img.offsetHeight;
      const scale =
        quarters % 2 && frame && width && height
          ? Math.min(frame.clientWidth / height, frame.clientHeight / width, 1)
          : 1;
      img.style.transform = `rotate(${quarters * 90}deg) scale(${scale})`;
    });
  }

  // A scanned album page, offering to be cut up. Null for anything the
  // detector did not find prints on, or that somebody has already decided
  // about — a panel that appears on every photograph is one people learn to
  // ignore.
  const splitting = a.kind !== "video" ? pageSplit(a, () => closeAsset()) : null;

  /**
   * The face boxes step aside while a rectangle is being drawn.
   *
   * Watched rather than wired: both overlays already say what they are doing
   * by toggling their own `hidden`, and an observer keeps that the single
   * source of truth. Calling into `editpanel` and `pagesplit` to tell this
   * layer to move would be two more couplings and a third place for the three
   * of them to disagree about who owns the pointer.
   */
  const rectangles = [splitting?.overlay, editing?.overlay].filter(
    (el): el is HTMLElement => !!el,
  );
  if (rectangles.length) {
    const settle = (): void => {
      const drawing = rectangles.some((el) => !el.hidden);
      facesLayer.classList.toggle("stood-down", drawing);
    };
    const watch = new MutationObserver(settle);
    for (const el of rectangles) {
      watch.observe(el, { attributes: true, attributeFilter: ["hidden"] });
    }
    settle();
  }

  const card = h(
    "div",
    { class: "lightbox glass", role: "dialog", "aria-label": a.caption ?? a.fileName },
    acts,
    h(
      "div",
      { class: "lbmedia" },
      // The overlay is positioned over the media, so they share a stacking
      // container sized by the photograph rather than by the panel.
      // Both overlays share the frame, which is sized by the photograph. Only
      // one is ever visible: splitting a page and cropping it are different
      // decisions about the same rectangle and would fight for the pointer.
      splitting || editing || a.kind !== "video"
        ? h(
            "div",
            { class: "psframe" },
            media,
            // Under the other two in the stack and taking no pointer events of
            // its own — only its boxes do. Cropping and splitting are
            // decisions about a rectangle and would fight the face boxes for
            // the click, so `hideFacesWhile` takes this layer away for as long
            // as either of them is up.
            facesLayer,
            ...(splitting ? [splitting.overlay] : []),
            ...(editing ? [editing.overlay] : []),
          )
        : media,
    ),
    h(
      "div",
      { class: "lbside" },
      title,
      description,
      canEditMetadata
        ? h(
            "div",
            { class: "lbmeta-actions" },
            h("button", { class: "btn xs", text: "Edit year", onclick: () => void editDate() }),
            store.can("vision.cloud.run")
              ? h("button", {
                  class: "btn xs ghost",
                  text: "Re-check year",
                  onclick: () => {
                    void api.queueAnalysis(store.libraryId, [a.id])
                      .then(() => toast("Queued a fresh date check."))
                      .catch((error: unknown) =>
                        toast(error instanceof Error ? error.message : "could not queue that", true),
                      );
                  },
                })
              : null,
          )
        : null,
      ...(splitting ? [splitting.el] : []),
      h("div", { class: "sectlbl", text: "Who is in it" }),
      people,
      // No "Darkroom" heading. A row of sliders under a photograph does not
      // need to be told what it is, and the label was a line of chrome in a
      // panel that has to hold the people, the darkroom and the metadata.
      ...(editing ? [editing.el] : []),
      meta,
    ),
  );

  host = h(
    "div",
    {
      class: "scrim",
      onclick: (e: MouseEvent) => {
        if (e.target === host) closeAsset();
      },
    },
    card,
  );
  document.body.appendChild(host);

  onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") closeAsset();
  };
  document.addEventListener("keydown", onKey);
}

/** Used by the shell to know whether Escape has already been spoken for. */
export const lightboxOpen = (): boolean => host !== null;
